import { useState } from "react";
import { Alert, Button, Card, Code, Group, Stack, Table, Text } from "@mantine/core";
import type { CardRefundCommand } from "@/api/cardRefunds";
import type { CardRefundDoc, CardRefundProofSubmissionDoc } from "@/firestore/cardRefunds";
import type { PaymentDoc, WalletDoc } from "@/firestore/types";
import { CardRefundStatus } from "@/components/CardRefundStatus";
import { DetailGrid } from "@/components/DetailGrid";
import { JsonExpander } from "@/components/JsonExpander";
import { Money, LocalMoney } from "@/components/Money";
import { Mono, ExternalMono } from "@/components/Mono";
import { CopyButton } from "@/components/CopyButton";
import { PageHeader } from "@/components/PageHeader";
import { RelatedLink } from "@/components/RelatedLink";
import { RelatedList } from "@/components/RelatedList";
import { Timestamp } from "@/components/Timestamp";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { COLLECTIONS } from "@/firestore/collections";
import { chainName } from "@/lib/chains";
import { txUrl } from "@/lib/explorer";
import { humanize } from "@/lib/format";
import { entityListPath } from "@/routes/paths";
import { CardRefundDialog, type RefundDialogAction } from "./CardRefundDialog";
import { refundMerchant, refundTransferAmount } from "./presentation";

export interface CardRefundDetailViewProps {
  refund: CardRefundDoc | null;
  loading: boolean;
  error: Error | null;
  stale: boolean;
  operatorId: string;
  canAct: boolean;
  pending: boolean;
  wallet: WalletDoc | null;
  walletLoading: boolean;
  walletError: Error | null;
  proofs: CardRefundProofSubmissionDoc[];
  proofLoading: boolean;
  proofError: Error | null;
  hasMoreProofs: boolean;
  onLoadMoreProofs(): void;
  activeProof: CardRefundProofSubmissionDoc | null;
  originalPayment: PaymentDoc | null;
  paymentLoading: boolean;
  paymentError: Error | null;
  onPaymentLookup(id: string): void;
  onCommand(command: CardRefundCommand, updatedAt: string): Promise<unknown>;
  onRefresh(): void;
}

export function CardRefundDetailView(props: CardRefundDetailViewProps) {
  const [dialog, setDialog] = useState<{ action: RefundDialogAction; version: string } | null>(
    null,
  );
  const d = props.refund;
  const crumbs = [{ label: "Card refunds", to: entityListPath("cardRefund") }];
  if (!d)
    return (
      <>
        <PageHeader title="Card refund" crumbs={crumbs} />
        {props.loading ? (
          <LoadingState />
        ) : props.error ? (
          <ErrorState error={props.error} />
        ) : (
          <EmptyState title="Refund not found" />
        )}
      </>
    );
  const instruction = d.payoutInstructions;
  const mine = !!props.operatorId && d.claimedBy === props.operatorId;
  const completed = d.fulfillmentState === "completed";
  const authorizing = d.fulfillmentState === "authorizing";
  const releasing = d.fulfillmentState === "releasing";
  const disabled = !props.canAct || props.pending || props.stale || props.loading || !!props.error;
  const canClaim =
    !disabled &&
    d.providerState === "completed" &&
    d.fulfillmentState === "unassigned" &&
    !d.reviewReasonCode &&
    !d.providerReviewReasonCode;
  const canRelease =
    !disabled &&
    mine &&
    !!d.authorizationRequest &&
    (authorizing || (d.fulfillmentState === "assigned" && !!instruction)) &&
    !d.submittedProof;
  const canSubmit =
    !disabled && mine && d.fulfillmentState === "assigned" && !!instruction && !d.submittedProof;
  const canCorrect =
    !disabled &&
    mine &&
    (d.fulfillmentState === "assigned" || d.fulfillmentState === "verifying") &&
    !!instruction &&
    !!d.submittedProof &&
    props.activeProof?.id === d.submittedProof.id &&
    props.activeProof?.state === "rejected";
  const canRetry =
    !disabled &&
    mine &&
    d.fulfillmentState === "verifying" &&
    !!instruction &&
    !!d.submittedProof &&
    props.activeProof?.id === d.submittedProof.id &&
    props.activeProof.state === "pending" &&
    props.activeProof.instructionVersion === instruction.version;
  const currentWalletMatches =
    !!instruction &&
    !props.walletLoading &&
    !props.walletError &&
    props.wallet?.id === instruction.wallet &&
    props.wallet.owner === d.owner &&
    props.wallet.state === "provisioned" &&
    props.wallet.mainAddress?.toLowerCase() === instruction.mainAddress.toLowerCase() &&
    props.wallet.chainId === instruction.chainId;
  const amount = instruction ? refundTransferAmount(instruction.amountMicroUsdc) : "";
  const authorization = d.authorization;
  const authorizationMatches =
    !!authorization &&
    !!d.authorizationRequest &&
    authorization.state === "granted" &&
    authorization.attemptId === d.authorizationRequest.id &&
    authorization.requestedCaseVersion === d.authorizationRequest.cardRefundVersion &&
    authorization.observationCount === d.authorizationRequest.observationCount &&
    authorization.reimbursement === d.id &&
    authorization.actor === d.claimedBy &&
    authorization.owner === d.owner &&
    authorization.amountMicroUsdc === d.amountMicroUsdc &&
    authorization.amountMicroUsdc === instruction?.amountMicroUsdc;
  const canUseInstructions =
    canSubmit &&
    currentWalletMatches &&
    !d.reviewReasonCode &&
    !d.providerReviewReasonCode &&
    d.providerState === "completed" &&
    authorizationMatches &&
    !!amount;
  const actionAllowed = (action: RefundDialogAction) =>
    action === "claim"
      ? canClaim
      : action === "release-unsent"
        ? canRelease
        : action === "submit-transfer"
          ? canSubmit
          : action === "correct-transfer-proof"
            ? canCorrect
            : action === "retry-verification"
              ? canRetry
              : !disabled && !d.originalPayment;
  function open(action: RefundDialogAction) {
    props.onPaymentLookup("");
    setDialog({ action, version: d!.updatedAt });
  }
  const evidence = d.fulfillmentEvidence;

  return (
    <>
      <PageHeader
        title={
          <Group>
            Card refund <CardRefundStatus state={d.queueState} />
          </Group>
        }
        subtitle={refundMerchant(d)}
        crumbs={crumbs}
        actions={
          <Button variant="default" size="xs" disabled={props.pending} onClick={props.onRefresh}>
            Refresh
          </Button>
        }
      />
      <Stack gap="lg">
        {props.stale ? (
          <Alert color="blue">
            Waiting for the latest recorded case. Actions resume when its live projection catches
            up.
          </Alert>
        ) : null}
        {props.error ? <ErrorState error={props.error} /> : null}
        {authorizing || releasing ? (
          <Alert
            color="blue"
            title={authorizing ? "Preparing payout instructions" : "Releasing assignment"}
          >
            {authorizing
              ? "The assignment is recorded. The backend is checking refund eligibility before preparing current Main instructions. Wait for the instructions before sending a transfer."
              : "The unsent release is recorded. Wait for confirmation before this case can be claimed again. Do not send a transfer."}
          </Alert>
        ) : null}
        {d.providerReviewReasonCode ? (
          <Alert title="Provider refund needs review" color="orange">
            <Text size="sm">{humanize(d.providerReviewReasonCode)}</Text>
            <Code>{d.providerReviewReasonCode}</Code>
            <Text size="sm" mt="xs">
              Do not send a new payment. Preserve and record evidence of any transfer already sent.
              {completed ? " The verified reimbursement remains completed." : ""}
            </Text>
          </Alert>
        ) : null}
        {d.reviewReasonCode ? (
          <Alert title="Needs review" color="orange">
            <Text size="sm">{humanize(d.reviewReasonCode)}</Text>
            <Code>{d.reviewReasonCode}</Code>
            <Text size="sm" mt="xs">
              Preserve any sent transaction hash. Review does not authorize another payment.
              {completed ? " The verified reimbursement remains completed." : ""}
            </Text>
          </Alert>
        ) : null}
        <Card withBorder padding="lg">
          <Stack>
            <DetailGrid
              fields={[
                { label: "Refund ID", value: <Mono value={d.id} short /> },
                { label: "Refund amount", value: <Money micro={d.amountMicroUsdc} fw={700} /> },
                {
                  label: "Merchant amount",
                  value: <LocalMoney minor={d.localAmountMinor} currency={d.localCurrency} />,
                },
                { label: "Provider status", value: humanize(d.providerState) },
                {
                  label: "USDC reimbursement",
                  value: completed
                    ? "Completed — transfer verified"
                    : authorizing
                      ? "Preparing payout instructions"
                      : releasing
                        ? "Releasing assignment"
                        : d.fulfillmentState === "verifying"
                          ? "Verifying received transfer"
                          : "Not completed",
                },
                { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
                { label: "Card", value: <RelatedLink entity="card" id={d.card} /> },
                {
                  label: "Historical wallet",
                  value: <RelatedLink entity="wallet" id={d.account} />,
                },
                {
                  label: "Assigned operator",
                  value: <RelatedLink entity="user" id={d.claimedBy} />,
                },
                { label: "Assigned at", value: <Timestamp value={d.claimedAt} absolute /> },
                {
                  label: "Provider settled",
                  value: <Timestamp value={d.providerCompletedAt} absolute />,
                },
              ]}
            />
            <Group>
              {d.fulfillmentState === "unassigned" ? (
                <Button disabled={!canClaim} onClick={() => open("claim")}>
                  Start processing
                </Button>
              ) : null}
              {d.claimedBy && !mine && !completed ? (
                <Text size="sm">
                  This refund is assigned to another operator. Do not send a payment.
                </Text>
              ) : null}
              {mine && (authorizing || d.fulfillmentState === "assigned") && !d.submittedProof ? (
                <Button
                  variant="default"
                  disabled={!canRelease}
                  onClick={() => open("release-unsent")}
                >
                  Release — no transfer sent
                </Button>
              ) : null}
            </Group>
            {d.providerState !== "completed" && !completed ? (
              <Text size="sm" c="dimmed">
                Wait for provider completion before paying this refund.
              </Text>
            ) : null}
          </Stack>
        </Card>

        {instruction ? (
          <Card withBorder padding="lg">
            <Stack>
              <Text fw={600}>
                {completed || d.submittedProof || authorizing || releasing
                  ? "Recorded payout instructions"
                  : "Manual payout instructions"}
              </Text>
              {!completed && !d.submittedProof ? (
                canUseInstructions ? (
                  <Text size="sm">
                    Send exactly this amount once from your reimbursement wallet to the user's Main
                    on the network below. Then record the transaction hash.
                  </Text>
                ) : (
                  <Alert color="orange">
                    These instructions are not available for a new transfer.
                    {!currentWalletMatches
                      ? " The current destination could not be confirmed or has changed."
                      : " Review the assignment and case status."}{" "}
                    If you already sent funds, preserve and submit the evidence; do not send again.
                  </Alert>
                )
              ) : null}
              <DetailGrid
                fields={[
                  {
                    label: "Exact USDC amount",
                    value: (
                      <Group>
                        <Text fw={700}>{amount || "Invalid amount"} USDC</Text>
                        {canUseInstructions ? <CopyButton value={amount} /> : null}
                      </Group>
                    ),
                  },
                  {
                    label: "Network",
                    value: `${chainName(instruction.chainId)} · chain ${instruction.chainId}`,
                  },
                  {
                    label: "Main recipient",
                    value: <Mono value={instruction.mainAddress} copy={canUseInstructions} />,
                    full: true,
                  },
                  {
                    label: "Native USDC contract",
                    value: <Mono value={instruction.tokenAddress} copy={canUseInstructions} />,
                    full: true,
                  },
                  {
                    label: "Payout wallet",
                    value: <RelatedLink entity="wallet" id={instruction.wallet} />,
                  },
                  {
                    label: "Instruction version",
                    value: <Mono value={instruction.version} short />,
                  },
                ]}
              />
              {mine && d.fulfillmentState === "assigned" && !d.submittedProof ? (
                <Button
                  disabled={!canSubmit}
                  onClick={() => open("submit-transfer")}
                  style={{ alignSelf: "flex-start" }}
                >
                  Verify transfer and mark done
                </Button>
              ) : null}
            </Stack>
          </Card>
        ) : null}

        {d.submittedProof ? (
          <Card withBorder padding="lg">
            <Stack>
              <Text fw={600}>
                {completed ? "Verified transfer" : "Submitted transfer evidence"}
              </Text>
              <Group>
                <ExternalMono
                  value={d.submittedProof.txHash}
                  href={txUrl(d.submittedProof.txHash, d.submittedProof.chainId)}
                />
                <Text size="sm">
                  Log {d.submittedProof.logIndex ?? "selected during verification"}
                </Text>
              </Group>
              {!completed ? (
                <Alert color={props.activeProof?.state === "rejected" ? "orange" : "blue"}>
                  {props.activeProof?.state === "rejected"
                    ? "This evidence was rejected. Correct the hash or log if needed; keep the existing transfer and assignment."
                    : "The submitted evidence is recorded. The backend is checking receipt confirmation and receiving ledger/activity records. Do not send another transfer."}
                </Alert>
              ) : null}
              {props.activeProof?.rejectionCode ? (
                <Code>{props.activeProof.rejectionCode}</Code>
              ) : null}
              {mine &&
              d.fulfillmentState === "verifying" &&
              props.activeProof?.state === "pending" ? (
                <Button
                  variant="default"
                  disabled={!canRetry}
                  onClick={() => open("retry-verification")}
                  style={{ alignSelf: "flex-start" }}
                >
                  Retry verification
                </Button>
              ) : null}
              {props.activeProof?.state === "rejected" && !completed && mine ? (
                <Button
                  variant="default"
                  disabled={!canCorrect}
                  onClick={() => open("correct-transfer-proof")}
                  style={{ alignSelf: "flex-start" }}
                >
                  Correct transfer evidence
                </Button>
              ) : null}
              {evidence ? (
                <DetailGrid
                  fields={[
                    { label: "Verified amount", value: <Money micro={evidence.amountMicroUsdc} /> },
                    {
                      label: "Network",
                      value: `${chainName(evidence.chainId)} · chain ${evidence.chainId}`,
                    },
                    {
                      label: "Received at",
                      value: <Timestamp value={evidence.blockTimestamp} absolute />,
                    },
                    {
                      label: "Completion recorded",
                      value: <Timestamp value={d.completedAt} absolute />,
                    },
                    { label: "Sender", value: <Mono value={evidence.fromAddress} />, full: true },
                    { label: "Recipient", value: <Mono value={evidence.toAddress} />, full: true },
                    {
                      label: "USDC contract",
                      value: <Mono value={evidence.tokenAddress} />,
                      full: true,
                    },
                    {
                      label: "Block / log",
                      value: `${evidence.blockNumber} / ${evidence.logIndex}`,
                    },
                    {
                      label: "Completed by",
                      value: <RelatedLink entity="user" id={d.fulfilledBy} />,
                    },
                    {
                      label: "Receiving ledger ID",
                      value: <Mono value={d.fulfillmentLedgerEntry} short />,
                    },
                    {
                      label: "Receiving activity ID",
                      value: <Mono value={d.fulfillmentActivityEntry} short />,
                    },
                  ]}
                />
              ) : null}
            </Stack>
          </Card>
        ) : null}

        <Card withBorder padding="lg">
          <Stack>
            <Group justify="space-between">
              <Text fw={600}>Original purchase</Text>
              {!d.originalPayment ? (
                <Button
                  size="xs"
                  variant="default"
                  disabled={disabled}
                  onClick={() => open("link-original-payment")}
                >
                  Link original payment
                </Button>
              ) : null}
            </Group>
            {d.originalPayment ? (
              <Group>
                <RelatedLink entity="payment" id={d.originalPayment} />
                <Text size="sm">
                  Confirmed by{" "}
                  {d.originalPaymentLink?.source === "provider" ? "provider reference" : "operator"}
                </Text>
                <Timestamp value={d.originalPaymentLink?.linkedAt} />
              </Group>
            ) : (
              <Text size="sm" c="dimmed">
                No confirmed original purchase. Refunds may be independent; matching merchant
                details alone are not proof.
              </Text>
            )}
            {d.legacyPayment ? (
              <Group>
                <Text size="sm">Converted negative payment (audit reference)</Text>
                <RelatedLink entity="payment" id={d.legacyPayment} />
              </Group>
            ) : null}
          </Stack>
        </Card>

        <Card withBorder padding="lg">
          <Stack>
            <Text fw={600}>Transfer evidence history</Text>
            {props.proofError ? (
              <ErrorState error={props.proofError} />
            ) : props.proofLoading && !props.proofs.length ? (
              <LoadingState />
            ) : !props.proofs.length ? (
              <Text size="sm" c="dimmed">
                No evidence submitted.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={700}>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Submission</Table.Th>
                      <Table.Th>Transaction / log</Table.Th>
                      <Table.Th>Verification</Table.Th>
                      <Table.Th>Operator</Table.Th>
                      <Table.Th>Submitted</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {props.proofs.map((proof) => (
                      <Table.Tr key={proof.id}>
                        <Table.Td>
                          <Mono value={proof.id} short />
                          {proof.supersedes ? (
                            <Group gap="xs">
                              <Text size="xs" c="dimmed">
                                Corrects
                              </Text>
                              <Mono value={proof.supersedes} short />
                            </Group>
                          ) : null}
                        </Table.Td>
                        <Table.Td>
                          <ExternalMono
                            value={proof.txHash}
                            href={txUrl(proof.txHash, proof.chainId)}
                          />
                          <Text size="xs">Log {proof.logIndex ?? "auto"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge state={proof.state} />
                          {proof.rejectionCode ? (
                            <Text size="xs">{proof.rejectionCode}</Text>
                          ) : null}
                        </Table.Td>
                        <Table.Td>
                          <RelatedLink entity="user" id={proof.submittedBy} />
                        </Table.Td>
                        <Table.Td>
                          <Timestamp value={proof.createdAt} absolute />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
            {props.hasMoreProofs ? (
              <Button
                variant="default"
                onClick={props.onLoadMoreProofs}
                loading={props.proofLoading}
              >
                Load more evidence
              </Button>
            ) : null}
          </Stack>
        </Card>
        {d.fulfillmentLedgerEntry ? (
          <RelatedList
            spec={{
              title: "Receiving ledger entry",
              entity: "ledgerEntry",
              collection: COLLECTIONS.ledgerEntries,
              fkField: "id",
              value: d.fulfillmentLedgerEntry,
            }}
          />
        ) : null}
        <Card withBorder padding="lg">
          <Stack>
            <Text fw={600}>Provider and audit details</Text>
            <DetailGrid
              fields={[
                { label: "Provider", value: humanize(d.provider) },
                { label: "Provider refund ID", value: <Mono value={d.foreignId} /> },
                { label: "Provider refund method", value: d.details?.refundMethod ?? "—" },
                {
                  label: "Refund case version",
                  value: <Timestamp value={d.sourceCaseVersion} absolute />,
                },
                { label: "Processed refund updates", value: d.sourceObservationCount },
                {
                  label: "Authorization attempt",
                  value: <Mono value={d.authorizationRequest?.id} short />,
                },
                {
                  label: "Authorization decision",
                  value: d.authorization
                    ? humanize(d.authorization.state)
                    : d.authorizationRequest
                      ? "Awaiting decision"
                      : "Not requested",
                },
                {
                  label: "Authorization reason",
                  value: d.authorization?.reasonCode ? humanize(d.authorization.reasonCode) : "—",
                },
                {
                  label: "Provider occurred",
                  value: <Timestamp value={d.providerOccurredAt} absolute />,
                },
                { label: "Created", value: <Timestamp value={d.createdAt} absolute /> },
                { label: "Updated", value: <Timestamp value={d.updatedAt} absolute /> },
              ]}
            />
            <JsonExpander value={d} />
          </Stack>
        </Card>
      </Stack>
      {dialog ? (
        <CardRefundDialog
          refund={d}
          action={dialog.action}
          expectedUpdatedAt={dialog.version}
          disabled={!actionAllowed(dialog.action)}
          pending={props.pending}
          activeProof={props.activeProof}
          originalPayment={props.originalPayment}
          paymentLoading={props.paymentLoading}
          paymentError={props.paymentError}
          onPaymentLookup={props.onPaymentLookup}
          onCommand={props.onCommand}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
